// Which school a school admin is working on (006 FR-038 to FR-041; research R3; data-model §2).
// One school: always it, no picker. Several: the id remembered on this device when the session
// still lists it, else the admin must choose. Only ids from the session's own grants are ever
// stored; the store is cleared on every sign-out (personal-data cleaner).
import { useCallback, useEffect, useSyncExternalStore } from "react";

import { deviceStorage } from "@/lib/deviceStorage";
import { useSession } from "@/session/hooks";
import type { SchoolIdentity } from "@/session/types";

export const ADMIN_SCHOOL_KEY = "akaiva.adminSchoolId";

export type AdminSchoolResolution =
  | { kind: "none" }
  | { kind: "single"; school: SchoolIdentity }
  | { kind: "chosen"; school: SchoolIdentity }
  | { kind: "needsChoice"; schools: readonly SchoolIdentity[] };

export function resolveAdminSchool(
  schools: readonly SchoolIdentity[],
  rememberedId: string | null,
): AdminSchoolResolution {
  if (schools.length === 0) return { kind: "none" };
  if (schools.length === 1) return { kind: "single", school: schools[0]! };
  const chosen = schools.find((s) => s.id === rememberedId);
  return chosen ? { kind: "chosen", school: chosen } : { kind: "needsChoice", schools };
}

type Storage = Pick<typeof deviceStorage, "get" | "set" | "remove">;

export type AdminSchoolStore = {
  /** The remembered id (null until loaded or when nothing is remembered). */
  get(): string | null;
  isReady(): boolean;
  load(): Promise<void>;
  /** Stores `id` only when it is one of `schools`; returns whether it was accepted. */
  choose(schools: readonly SchoolIdentity[], id: string): boolean;
  /** Drops a remembered id the session no longer lists (US6-5). */
  reconcile(schools: readonly SchoolIdentity[]): void;
  clear(): void;
  subscribe(listener: () => void): () => void;
};

export function createAdminSchoolStore(
  storage: Storage = deviceStorage,
): AdminSchoolStore {
  let remembered: string | null = null;
  let ready = false;
  let loading: Promise<void> | null = null;
  const listeners = new Set<() => void>();
  const emit = () => listeners.forEach((l) => l());
  const setRemembered = (id: string | null) => {
    if (id === remembered) return;
    remembered = id;
    if (id === null) void storage.remove(ADMIN_SCHOOL_KEY);
    else void storage.set(ADMIN_SCHOOL_KEY, id);
    emit();
  };
  return {
    get: () => remembered,
    isReady: () => ready,
    load() {
      loading ??= storage.get(ADMIN_SCHOOL_KEY).then((value) => {
        if (!ready) {
          remembered = value && value.length > 0 ? value : null;
          ready = true;
          emit();
        }
      });
      return loading;
    },
    choose(schools, id) {
      if (!schools.some((s) => s.id === id)) return false;
      ready = true;
      setRemembered(id);
      return true;
    },
    reconcile(schools) {
      if (!ready || remembered === null || schools.length === 0) return;
      if (!schools.some((s) => s.id === remembered)) setRemembered(null);
    },
    clear() {
      ready = true;
      setRemembered(null);
      void storage.remove(ADMIN_SCHOOL_KEY);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

/** The app's single store (module level). */
export const appAdminSchool = createAdminSchoolStore();

/** Personal-data cleaner: every sign-out cause (FR-040). */
export function clearAdminSchool(): void {
  appAdminSchool.clear();
}

export type UseAdminSchool = {
  resolution: AdminSchoolResolution;
  /** False while the remembered id is still being read (several schools only). */
  ready: boolean;
  choose: (id: string) => boolean;
};

const EMPTY: readonly SchoolIdentity[] = [];

export function useAdminSchool(
  store: AdminSchoolStore = appAdminSchool,
): UseAdminSchool {
  const session = useSession();
  const schools =
    session.status === "signedIn" ? session.schoolAdminSchools : EMPTY;
  const remembered = useSyncExternalStore(store.subscribe, store.get, store.get);
  const ready = useSyncExternalStore(
    store.subscribe,
    store.isReady,
    store.isReady,
  );
  useEffect(() => {
    if (schools.length > 1) void store.load();
  }, [schools.length, store]);
  useEffect(() => {
    store.reconcile(schools);
  }, [schools, store, ready]);
  const choose = useCallback(
    (id: string) => store.choose(schools, id),
    [schools, store],
  );
  return {
    resolution: resolveAdminSchool(schools, remembered),
    ready: schools.length <= 1 || ready,
    choose,
  };
}
